import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getPatientById,
  getPatientClinicalForm,
  logClinicalFormPrint,
} from "../../../../services/patientService";
import PrintShell from "../../../../components/UI/PrintShell";
import ClinicalFormPrintBlock from "./ClinicalFormPrintBlock";
import { getDedicatedFormPage } from "./dedicatedForms";

/**
 * Opened in its own tab from the forms list. Renders the very same component
 * the on-screen page renders — just frozen and stripped of chrome — so what
 * comes out of the printer (or Save as PDF) is the form itself.
 *
 * New form types need no changes here: registering them in `dedicatedForms`
 * gives them a print view too, and anything unregistered still prints through
 * the generic sheet.
 */
const PatientClinicalFormPrintPage = () => {
  const { patientId, formId } = useParams();
  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  /** A dedicated form loads its own detail record and tells us when it lands. */
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getPatientById(patientId),
      getPatientClinicalForm(patientId, formId),
    ])
      .then(([patientRes, formRes]) => {
        if (cancelled) return;
        const patientData = patientRes.result || patientRes.data || patientRes;
        setPatient(patientData.patient || patientData);
        setForm(formRes.result?.form || formRes.form);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load form");
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, formId]);

  const handleContentReady = useCallback(() => setContentReady(true), []);

  // A printed chart page leaves the system, so the trail records it. Logging is
  // best-effort: it must never block the print dialog.
  const handlePrinted = useCallback(() => {
    logClinicalFormPrint(patientId, formId).catch(() => {});
  }, [patientId, formId]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-6">
        <div className="max-w-sm rounded-xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-800">
            This form could not be loaded
          </p>
          <p className="mt-1 text-xs text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!patient || !form) {
    return (
      <div className="min-h-screen animate-pulse bg-slate-100 py-10">
        <div className="mx-auto h-[700px] w-full max-w-4xl rounded-xl border border-slate-200 bg-white" />
      </div>
    );
  }

  const isDedicated = Boolean(getDedicatedFormPage(form.formType));
  const patientName =
    [patient.firstName, patient.middleName, patient.lastName]
      .filter(Boolean)
      .join(" ") || patient.patientName;

  return (
    <PrintShell
      title={form.formName}
      subtitle={`${patientName || "Patient"} · MRN ${patient.mrn || "—"}`}
      ready={isDedicated ? contentReady : true}
      onPrinted={handlePrinted}
    >
      <ClinicalFormPrintBlock
        patient={patient}
        clinicalForm={form}
        onReady={handleContentReady}
      />
    </PrintShell>
  );
};

export default PatientClinicalFormPrintPage;
